<template>
  <div
    ref="container"
    class="cell-editor-container">
    <div
      class="full-screen"
      @click="cancelEditing" />
    <div
      ref="editor"
      class="cell-editor"
      :class="{
        small: size == 'small',
        medium: size == 'medium' || isDateType,
        large: size == 'large',
      }">
      <!-- LISTS -->
      <template v-if="isListType">
        <Multiselect
          v-if="column.editablePropertyAcceptsMultipleValues"
          v-model="selectedListItems"
          :track-by="column.sourceID"
          :label="column.sourceLabel"
          :options="options || []"
          :searchable="true"
          :show-labels="false"
          :allow-empty="true"
          :multiple="true"
          :close-on-select="false"
          :group-values="column.sourceGroupValues"
          :group-label="column.sourceGroupLabel"
          @input="onMultiselectInputChanged"
          @open="onMultiselectInputChanged"
          @keyup.enter="commit"
          @keyup.escape="cancelEditing" />
        <Multiselect
          v-else
          v-model="selectedListItem"
          :track-by="column.sourceID"
          :label="column.sourceLabel"
          :options="options || []"
          :searchable="true"
          :show-labels="false"
          :allow-empty="false"
          :multiple="false"
          :close-on-select="true"
          :group-values="column.sourceGroupValues"
          :group-label="column.sourceGroupLabel"
          @keyup.enter="commit"
          @keyup.escape="cancelEditing" />
      </template>
      <!-- DATES -->
      <template v-else-if="isDateType">
        <DatePicker
          :date="value"
          name="inlineEditingDate"
          :min-date="today"
          :auto-close="true"
          class="daterangepicker-input"
          :clearable="false"
          @date-changed="(date) => (value = date)" />
      </template>
      <!-- NUMBERS -->
      <template v-else-if="isNumberType">
        <input
          ref="inputText"
          v-model="value"
          type="number"
          class="form-control"
          @keyup.enter="commit"
          @keyup.escape="cancelEditing" />
      </template>
      <!-- BOOLEAN -->
      <template v-else-if="isBooleanType">
        <TabsSlider
          :selected-tab-i-d="value ? 'true' : 'false'"
          :tabs="booleanOptions"
          width="15rem"
          @selected-tab-changed="(x) => (value = x.id === 'true')" />
      </template>
      <template v-else>
        <input
          ref="inputText"
          v-model="textValue"
          type="text"
          class="form-control"
          @keyup.enter="commit"
          @keyup.escape="cancelEditing" />
      </template>
      <button
        class="btn btn-sm btn-no-text btn-success-reverse commit-button"
        @click="commit">
        <IconCheck2 />
      </button>
      <button
        class="btn btn-sm btn-no-text btn-danger-reverse cancel-button"
        @click="cancelEditing">
        <IconClose />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, onBeforeMount, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
  import Multiselect from 'vue-multiselect';

  import DatePicker from '@/common/components/controls/DatePicker.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import TableEvents from '@/common/components/controls/table/TableEvents.enum';
  import TabsSlider from '@/common/components/controls/TabsSlider.vue';
  import { IconCheck2, IconClose } from '@/common/icons/monochrome';
  import { LineItem, QueryParams } from '@/common/models';
  import UnitLanguage from '@/common/models/unit/UnitLanguage';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { camelToSnakeCase, copyValue, getAppInstance, toServerDate } from '@/globals';
  import rootStore from '@/store';
  import { waitUntil } from '@/utils/DelayHelper';
  import UserNotifications from '@/utils/UserNotifications';
  import Validator from '@/validator/Validator';
  import i18n from '@/vueI18n';

  enum EditorType {
    List,
    Date,
    Number,
    Boolean,
    Text,
  }

  const DATE_COLUMN_TYPES = [TableColumnTypes.datetime, TableColumnTypes.date];

  const NUMBER_COLUMN_TYPES = [TableColumnTypes.currency, TableColumnTypes.integer, TableColumnTypes.float];

  type InlineEditedEntity = {
    id;
    toApi();
    clone?(): InlineEditedEntity;
    isInlineEdited?: boolean;
    constructor: {
      fromApi: (apiObj) => InlineEditedEntity;
    };
  };

  interface IProps {
    entity: InlineEditedEntity;
    column: TableColumn;
    size?: 'small' | 'medium' | 'large';
    commitCallback?: (payload?) => void;
    cancelCallback?: () => void;
    showWarningBeforeSave?: boolean;
    handleInlineEditUpdateOutsideOfTable?: (entity: InlineEditedEntity, value) => void;
  }

  const {
    entity,
    column,
    size = 'small',
    commitCallback,
    cancelCallback,
    showWarningBeforeSave,
    handleInlineEditUpdateOutsideOfTable,
  } = defineProps<IProps>();

  const emits = defineEmits([TableEvents.CANCEL_FIELD_EDITING, TableEvents.COMMIT_FIELD_EDITING]);

  const container = useTemplateRef<HTMLElement>('container');
  const editor = useTemplateRef<HTMLElement>('editor');
  const inputText = useTemplateRef<HTMLInputElement>('inputText');

  const hasErrors = ref(false);
  const optionsCached = ref();
  const value = ref();
  const selectedListItem = ref();
  const selectedListItems = ref([]);
  const today = ref(DateTime.local().startOf('day'));
  const originalValue = ref();

  const booleanOptions = computed(() => [
    {
      id: 'true',
      name: capitalize(i18n.global.t('actions.yes')),
    },
    {
      id: 'false',
      name: capitalize(i18n.global.t('actions.no')),
    },
  ]);

  const textValue = computed<string>({
    get: () => {
      return value.value;
    },
    set: (val: string) => {
      value.value = val.trim();
    },
  });

  const options = computed(() => optionsCached.value);

  const updatableEntity = computed(() => {
    const prop = column.editablePropertyName || column.property;
    const fragments = prop.split('.');
    let value = entity;
    for (let i = 0; i < fragments.length - 1; i += 1) {
      value = value[fragments[i]];
    }
    return value;
  });

  const updatableProperty = computed(() => {
    return column.sourceFilterProperty != null
      ? column.sourceFilterProperty.split('.').slice(-1)[0]
      : column.property.split('.').slice(-1)[0];
  });

  const editorType = computed<EditorType>(() => {
    if (column.source != null) {
      return EditorType.List;
    }
    if (DATE_COLUMN_TYPES.includes(column.type)) {
      return EditorType.Date;
    }
    if (NUMBER_COLUMN_TYPES.includes(column.type)) {
      return EditorType.Number;
    }
    if (column.type === TableColumnTypes.boolean) {
      return EditorType.Boolean;
    }
    return EditorType.Text;
  });

  const isListType = computed(() => editorType.value === EditorType.List);
  const isDateType = computed(() => editorType.value === EditorType.Date);
  const isNumberType = computed(() => editorType.value === EditorType.Number);
  const isBooleanType = computed(() => editorType.value === EditorType.Boolean);

  watch(
    () => entity,
    () => readValue(),
  );

  watch(
    () => column,
    () => readValue(),
  );

  watch(selectedListItem, (val) => {
    if (val == null) return;
    value.value = val[column.sourceID];
  });

  watch(selectedListItems, (val) => {
    if (val == null) return;
    value.value = val.map((x) => x[column.sourceID]);
  });

  watch(
    () => showWarningBeforeSave,
    () => {
      if (column.editableClassName === 'Creative' && column.property === 'iabCategoriesFormatted') {
        value.value = selectedListItems.value.map((x) => x.id);
      }
    },
  );

  onBeforeMount(() => {
    readValue();
    // Save the original value so we can revert if an error occurs when saving
    originalValue.value = copyValue(value.value);
    if (isListType.value) {
      loadOptions();
    }
  });

  onMounted(async () => {
    // Because this component is mounted dynamically from Table.vue there is a slight delay between
    // the time it is mounted and the time it's added to the DOM, this is why the call to focus()
    // needs to be delayed.
    await waitUntil(
      () => {
        const input = container.value?.querySelector('input');
        if (input == null) {
          return false;
        }

        input.focus();
        return true;
      },
      50,
      200,
    );
    /**`
     * Cancel editing if the user navigates to another route
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event
     */
    window.addEventListener('popstate', cancelEditing);
    // unset the minimum width if boolean type
    // change position to inherit if List
    if (isBooleanType.value) {
      editor.value.style.minWidth = 'unset';
    } else if (isListType.value) {
      editor.value.style.position = 'inherit';
    }
    GlobalEventBus.$on(GlobalEvents.inlineEditError, () => {
      hasErrors.value = true;
    });
  });

  onBeforeUnmount(() => {
    window.removeEventListener('popstate', cancelEditing);
  });

  function cancelEditing() {
    emits(TableEvents.CANCEL_FIELD_EDITING);
    if (cancelCallback) {
      cancelCallback();
    }
  }

  function readValue() {
    if (column == null || entity == null) {
      value.value = null;
    } else {
      const prop = column.editablePropertyName || column.property;
      const fragments = prop.split('.');
      let cursor = entity;
      fragments.forEach((f) => {
        cursor = cursor[f];
      });
      value.value = cursor;
    }
  }

  function onMultiselectInputChanged() {
    GlobalEventBus.$emit(GlobalEvents.onMultiSelectSelectionChanged, column);
  }

  async function loadOptions() {
    if (!optionsCached.value) {
      if (column.inlineEditingOptionsSource != null) {
        optionsCached.value = await column.inlineEditingOptionsSource();
      } else if (column.source instanceof Function) {
        const sourceResponse = await QueryParams.all().run(column.source);
        if (sourceResponse != null) {
          if (typeof sourceResponse === 'object' && 'items' in sourceResponse) {
            optionsCached.value = sourceResponse.items;
          } else {
            optionsCached.value = sourceResponse;
          }
        }
      } else if (column.source instanceof Promise) {
        optionsCached.value = await column.source;
      } else {
        optionsCached.value = column.source;
      }
    }

    const flattenedOptions = column.sourceGroupValues
      ? options.value.map((x) => x[column.sourceGroupValues]).flat()
      : options.value;

    if (column.editablePropertyAcceptsMultipleValues) {
      if (column.label === 'Languages') {
        selectedListItems.value = flattenedOptions.filter((x) =>
          value.value
            .map((v) => {
              if (v instanceof UnitLanguage) {
                return v.languageID;
              }
              return v;
            })
            .includes(x[column.sourceID]),
        );
      } else {
        selectedListItems.value =
          options.value == null || value.value == null
            ? []
            : flattenedOptions.filter((x) =>
                value.value
                  .map((v) => (v[column.sourceID] != null ? v[column.sourceID] : v))
                  .includes(x[column.sourceID]),
              );
      }
    } else {
      selectedListItem.value =
        options.value == null || value.value == null
          ? null
          : flattenedOptions.find((x) => x[column.sourceID] === value.value);
    }
  }

  async function commit() {
    if (handleInlineEditUpdateOutsideOfTable) {
      await handleInlineEditUpdateOutsideOfTable(entity, value.value);
      commitCallback();
      return;
    }

    const entityClass = column.editableClassName;
    const propertyName = `${entityClass[0].toLowerCase()}${entityClass.slice(1)}`;

    // Create a copy of the entity to validate the mutation
    // If the mutation is successful, we will apply the changes to the original entity
    let entityClone = null;

    if (updatableEntity.value instanceof LineItem) {
      // Using copy here because clone resets the line item id to -1
      entityClone = updatableEntity.value.copy();
    } else {
      entityClone =
        updatableEntity.value.clone != null
          ? updatableEntity.value.clone()
          : updatableEntity.value.constructor.fromApi(updatableEntity.value.toApi());
    }

    //
    // Call mutation on cloned entity.
    //
    let normalizedValue = value.value;
    if (column.preMutationTransform) {
      normalizedValue = column.preMutationTransform(value.value, entity);
    } else {
      switch (column.type) {
        case TableColumnTypes.float:
        case TableColumnTypes.currency:
        case TableColumnTypes.coordinate:
          try {
            normalizedValue = parseFloat(value.value);
          } catch {
            console.error("Looks like you're trying to parse the wrong type");
          }
          break;
        case TableColumnTypes.integer:
          try {
            normalizedValue = parseInt(value.value, 10);
          } catch {
            console.error("Looks like you're trying to parse the wrong type");
          }
          break;
        default:
          break;
      }
    }
    const payload = {
      value: normalizedValue,
    };
    payload[propertyName] = entityClone;

    // Apply mutation to cloned entity for validation
    executeMutation(payload);

    //
    // Validate
    //
    const validator = new Validator(getAppInstance());
    validator.init(entityClone);
    try {
      const optionalPayload =
        column.getExtraValidation != null
          ? column.getExtraValidation(entityClone, validator, payload)
          : entityClone.getExtraValidation != null
            ? entityClone.getExtraValidation()
            : undefined;
      await validator.validateAll(entityClone, optionalPayload);
    } catch {
      hasErrors.value = true;
      const failedToSaveMessage = i18n.global.t(`messages.failedToSave${entityClass}`);
      const message = `${failedToSaveMessage}<br/>${validator.messageBag.htmlErrorList}`;
      UserNotifications.error({
        title: i18n.global.t('messages.missingRequiredValue'),
        message,
      });
      return;
    }

    hasErrors.value = false;

    //
    // Mutate original entity
    //
    payload[propertyName] = updatableEntity.value;
    executeMutation(payload);

    //
    // Call vuex action.
    //
    const actionSuccessful = await executeAction(propertyName, entityClass);
    if (!actionSuccessful || hasErrors.value) {
      // If the action failed, we need to revert the changes made to the original entity
      const originalPayload = {
        value: originalValue.value,
        [propertyName]: updatableEntity.value,
      };
      executeMutation(originalPayload);
      return;
    }

    if (column.postMutationCallback != null) {
      //
      // Update the property bound to the cell (e.g.: accountName)
      //
      if (column.editablePropertyAcceptsMultipleValues) {
        column.postMutationCallback(selectedListItems.value || value.value, entity);
      } else {
        column.postMutationCallback(selectedListItem.value || value.value, entity);
      }
    }

    emits(TableEvents.COMMIT_FIELD_EDITING, payload);
    if (commitCallback) {
      commitCallback(payload);
    }
  }

  function executeMutation(payload: { value: any; [key: string]: any }) {
    if (column.mutation) {
      if (typeof column.mutation === 'string') {
        rootStore.commit(column.mutation, payload);
      } else {
        column.mutation(payload);
      }
    }
  }

  async function executeAction(propertyName: string, entityClass: string): Promise<boolean> {
    try {
      const val = column.preActionTransform ? column.preActionTransform(value.value) : value.value;
      if (column.action == null) {
        const instance = {
          id: updatableEntity.value.id, // Used to figure out the id is the route.
          [`${camelToSnakeCase(propertyName)}_id`]: updatableEntity.value.id,
          [updatableProperty.value]: val,
        };

        await rootStore.dispatch(`orgApp/update${entityClass}`, {
          instance,
          showLoader: false,
        });
      } else {
        updatableEntity.value.isInlineEdited = true;
        if (showWarningBeforeSave) {
          GlobalEventBus.$emit(`show${column.editableClassName}WarningModal`, commit);
          return false;
        }

        const instance = {
          isInlineEdited: true,
          id: updatableEntity.value.id,
        };
        if (column.type === 'date') {
          instance[updatableProperty.value] = toServerDate(val);
        } else {
          instance[updatableProperty.value] = val;
        }
        await column.action(instance);
      }
      return true;
    } catch {
      return false;
    }
  }
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;

  .cell-editor-container {
    position: relative;
    width: 100%;
    height: 4rem;
    z-index: 5;
  }

  .cell-editor {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    z-index: 10;
    padding: 0.5rem;
    background: #ffffff;
    box-shadow: 0 0 0.8rem #00000033;
    margin-left: -0.3rem;
    margin-top: -0.2rem;
    border-radius: 0.6rem;

    .multiselect {
      min-width: 20rem;
    }

    animation-name: flipVertical;
    animation-duration: 0.5s;
    animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
    animation-fill-mode: backwards;

    > * {
      margin-left: 0.5rem;

      &:first-child {
        margin-left: 0;
      }
    }
  }

  .iabCategoriesContainer,
  .languagesContainer {
    .cell-editor {
      max-width: 25%;
    }
  }

  .small {
    min-width: calc(100% + 3rem);
  }

  .medium {
    min-width: calc(100% + 15rem);
  }

  .large {
    min-width: calc(100% + 30rem);
  }

  #app {
    .full-screen {
      opacity: 0.01;
      z-index: 5;
    }
  }
</style>
